import CloseOutlined from '@ant-design/icons/CloseOutlined'
import EditOutlined from '@ant-design/icons/EditOutlined'
import SaveOutlined from '@ant-design/icons/SaveOutlined'
import { Button, Popconfirm } from 'antd'
import CantileverCard from 'components/CantileverCard'
import { useRolodexEdit } from 'features/fde'
import React, { useCallback, useState } from 'react'
import styled from 'styled-components'
import CantileverTheme from 'styles/cantileverTheme'
import { GeckoEntry } from 'types'

import { ObjectPropertiesView } from './ObjectPropertiesView'

interface ObjectPropertiesCardProps {
  entry: GeckoEntry
  forceRefresh: () => Promise<void>
}

export const ObjectPropertiesCard: React.FC<ObjectPropertiesCardProps> = ({
  entry,
  forceRefresh,
}) => {
  const [draftData, setDraftData] = useState<
    Record<string, unknown> | undefined
  >(undefined)
  const [isLoading, setIsLoading] = useState(false)

  const { rolodexPut } = useRolodexEdit()
  const handleSave = useCallback(async () => {
    setIsLoading(true)
    try {
      await rolodexPut([
        {
          id: entry.id,
          type: entry.type,
          data: draftData ?? entry.data,
          tags: entry.tags,
        },
      ])
      await forceRefresh()
    } finally {
      setDraftData(undefined)
      setIsLoading(false)
    }
  }, [
    draftData,
    entry.data,
    entry.id,
    entry.tags,
    entry.type,
    forceRefresh,
    rolodexPut,
  ])

  return (
    <CantileverCard>
      <CantileverCard.CardHeader
        leadingContent='Properties'
        trailingContent={
          <ObjectPropertiesCardEditButton
            isEditing={!!draftData}
            setIsEditing={(isEditing) =>
              setDraftData(isEditing ? entry.data : undefined)
            }
            onSave={handleSave}
            isLoading={isLoading}
          />
        }
      />
      <CantileverCard.Section topPadding bottomPadding>
        <ObjectPropertiesView
          data={draftData ?? entry.data}
          entryType={entry.type}
          setData={draftData ? setDraftData : undefined}
          isLoading={isLoading}
        />
      </CantileverCard.Section>
    </CantileverCard>
  )
}

interface ObjectPropertiesCardEditButtonProps {
  isEditing: boolean
  setIsEditing: (isEditing: boolean) => void
  onSave?: () => Promise<void>
  isLoading?: boolean
}

const ObjectPropertiesCardEditButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-right: 0.25rem;
  gap: 0.25rem;
`

const ObjectPropertiesCardEditButton: React.FC<
  ObjectPropertiesCardEditButtonProps
> = ({ isEditing, setIsEditing, onSave, isLoading }) => {
  return !isEditing ? (
    <ObjectPropertiesCardEditButtonContainer>
      <Button
        onClick={() => setIsEditing(true)}
        type='primary'
        icon={<EditOutlined />}
        style={{ backgroundColor: CantileverTheme.colors.grayscale['600'] }}
        loading={isLoading}>
        Edit
      </Button>
    </ObjectPropertiesCardEditButtonContainer>
  ) : (
    <ObjectPropertiesCardEditButtonContainer>
      <Button
        onClick={() => setIsEditing(false)}
        icon={<CloseOutlined />}
        type='primary'>
        Cancel
      </Button>
      {onSave && (
        <Popconfirm
          title='Save to Rolodex?'
          icon={<SaveOutlined />}
          onConfirm={onSave}>
          <Button
            type='primary'
            icon={<SaveOutlined />}
            loading={isLoading}
            style={{
              backgroundColor: CantileverTheme.colors.blue['500_base'],
            }}>
            Save
          </Button>
        </Popconfirm>
      )}
    </ObjectPropertiesCardEditButtonContainer>
  )
}
